import React, { useCallback, useEffect } from 'react';

import { Dialog, DialogProps } from '@mui/material';
import { Row, SPWCancelButton } from 'spw-shared/globalComponents';
import {
  SPWMainButton,
  SPWOutlinedMainButton,
} from 'spw-shared/globalComponents/buttons';
import { colors } from 'spw-theme';
import { ActionsDialogContainer } from './styles';

type ButtonVariant = 'main' | 'main-outlined' | 'delete' | 'cancel';

export type IActionsDialog = Omit<
  DialogProps,
  'open' | 'onClose' | 'disableScrollLock' | 'title'
> & {
  title?: string | JSX.Element;
  isOpen: boolean;
  description?: string | JSX.Element;
  containerClassName?: string;
  ContainerComponent?: typeof ActionsDialogContainer;
  onUnmount?: () => void;
  onClose?: () => void;
  width?: string;
  actions?: {
    text: string;
    variant: ButtonVariant;
    onClick: () => void;
    testingId?: string;
    className?: string;
  }[];
};

const ActionsDialog: React.FunctionComponent<IActionsDialog> = ({
  title,
  onClose,
  isOpen,
  description,
  actions,
  containerClassName,
  ContainerComponent = ActionsDialogContainer,
  onUnmount,
  width,
  ...props
}: IActionsDialog) => {
  const handleClose = useCallback<DialogProps['onClose']>(
    (_, reason) => {
      if (reason !== 'backdropClick' && onClose) onClose();
    },
    [onClose]
  );

  useEffect(() => {
    return () => {
      if (onUnmount) {
        onUnmount();
      }
    };
  }, [onUnmount]);

  return (
    <Dialog
      {...props}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      disableScrollLock
    >
      <ContainerComponent className={containerClassName} $width={width}>
        <div className="expanded">
          <div className="title">{title}</div>
          <div className="description">{description}</div>
        </div>

        <Row className="actions-row">
          {actions?.map((action) => {
            if (action.variant === 'delete')
              return (
                <SPWMainButton
                  key={action.text}
                  style={{ backgroundColor: colors.red }}
                  onClick={action.onClick}
                  className={`margin-right-16 buttons-padding ${
                    action.className ?? ''
                  }`}
                  data-testid={action.testingId}
                >
                  {action.text}
                </SPWMainButton>
              );
            if (action.variant === 'main')
              return (
                <SPWMainButton
                  key={action.text}
                  onClick={action.onClick}
                  className={`margin-right-16 buttons-padding ${
                    action.className ?? ''
                  }`}
                  data-testid={action.testingId}
                >
                  {action.text}
                </SPWMainButton>
              );
            if (action.variant === 'main-outlined')
              return (
                <SPWOutlinedMainButton
                  key={action.text}
                  onClick={action.onClick}
                  className={`margin-right-16 buttons-padding ${
                    action.className ?? ''
                  }`}
                  data-testid={action.testingId}
                >
                  {action.text}
                </SPWOutlinedMainButton>
              );
            return (
              <SPWCancelButton
                key={action.text}
                onClick={action.onClick}
                className={`margin-right-16 buttons-padding ${
                  action.className ?? ''
                }`}
                color="primary"
                style={{ color: colors.black, borderColor: colors.black }}
                data-testid={action.testingId}
              >
                {action.text}
              </SPWCancelButton>
            );
          })}
        </Row>
      </ContainerComponent>
    </Dialog>
  );
};

export default ActionsDialog;
